import React, {ChangeEvent, useEffect, useMemo, useState} from "react"
import { useAppDispatch, useAppSelector } from "../../../../hooks/redux"
import { useDebounce } from 'usehooks-ts'
import useWindowSize from "../../../../hooks/useWindowSize"
import {fetchBrigades, getBrigadeStatuses, setCurrentPage} from "../../../../store/actions/brigadeActions"
import Button from "../../../generic/buttons/Button/Button"
import Loading from "../../Loading/Loading"
import BrigadeLine from "../../Lines/BrigadeLine/BrigadeLine"
import Pagination from "../../../generic/navigation/Pagination"
import ModalSuccess from "../../modals/ModalSuccess/ModalSuccess"
import ModalEditBrigade from "../../modals/ModalEditBrigade/ModalEditBrigade"
import { IDataTableTitle } from "../../../../models"
import Search from "../../Search"
import {IFetchBrigadeParams} from "../../../../models/brigade"
import classes from "../Handbook.module.scss"

export default function Brigades() {

  const dispatch = useAppDispatch()
  const windowSize = useWindowSize()
  const {list, isLoading, pages, totalCount, pageSize, fetchError, currentPage} = useAppSelector(state => state.brigade)
  const [modalCreateShown, modalCreateToggle] = useState(false)
  const [modalCreateSuccessShown, modalCreateSuccessToggle] = useState(false)
  const [modalEditSuccessShown, modalEditSuccessToggle] = useState(false)
  const [modalDeleteSuccessShown, modalDeleteSuccessToggle] = useState(false)
  const [query, setQuery] = useState("")
  const debouncedSearchQuery = useDebounce<string>(query, 500)

  const pageChangeHandler = ({selected}: {selected: number}) => {
    dispatch(setCurrentPage(selected + 1))
  }

  const changeSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const fetchParams = useMemo(() => {
    const obj: IFetchBrigadeParams = {
      page: currentPage,
      pageSize
    }
    if (debouncedSearchQuery) {
      obj.query = debouncedSearchQuery
      obj.pageSize = 0
      obj.page = 1
    }
    return obj
  }, [currentPage, pageSize, debouncedSearchQuery])

  useEffect(() => {
    dispatch(fetchBrigades(fetchParams))
  }, [dispatch, fetchParams])

  useEffect(() => {
    dispatch(getBrigadeStatuses())
  }, [dispatch])

  const titles: IDataTableTitle[] = [
    {
      id: 1,
      name: "Бригада"
    },
    {
      id: 2,
      name: "Сотрудники"
    },
    {
      id: 3,
      name: "Статус",
      className: classes.data_cell_status
    },
    {
      id: 4,
      name: "",
      className: classes.data_cell_menu
    },
  ]

  const headerFieldsTemplate = titles.map((item) => {

    const cellCls = [classes.data_cell]

    if (item.className) {
      cellCls.push(item.className)
    }

    return (
      <div
        className={cellCls.join(" ")}
        key={item.id}
      >
        <span>{item.name}</span>
      </div>
    )
  })

  const brigadeLines = useMemo(
    () => list.map(brigade => (
      <BrigadeLine
        key={brigade.id}
        brigade={brigade}
        editSuccess={modalEditSuccessToggle}
        deleteSuccess={modalDeleteSuccessToggle}
      />
    )),
    [list]
  )

  const dataTemplate = !isLoading && totalCount > 0 &&
    <div className={classes.data}>
      {windowSize.width >= 768 &&
        <div className={classes.data_header}>
          {headerFieldsTemplate}
        </div>
      }
      {brigadeLines}
    </div>

  const emptyTemplate = !isLoading && totalCount === 0 && !fetchError &&
    <div className={classes.empty}>
      <div className={classes.empty_text}>
        {"Бригады не найдены"}
      </div>
    </div>

  const errorTemplate = !isLoading && fetchError &&
    <div className={classes.error}>
      <div className={classes.error_text}>
        {fetchError}
      </div>
    </div>

  const modalCreate = modalCreateShown &&
    <ModalEditBrigade
      close={() => modalCreateToggle(false)}
      editSuccess={modalCreateSuccessToggle}
    />

  const modalCreateSuccess = modalCreateSuccessShown &&
    <ModalSuccess
      close={() => modalCreateSuccessToggle(false)}
      title={"Бригада создана!"}
    />

  const modalUpdateSuccess = modalEditSuccessShown &&
    <ModalSuccess
      close={() => modalEditSuccessToggle(false)}
      title={"Бригада обновлена!"}
    />

  const modalDeleteSuccess = modalDeleteSuccessShown &&
    <ModalSuccess
      headerIconColor={"red"}
      close={() => modalDeleteSuccessToggle(false)}
      title={"Бригада удалена!"}
    />

  return (
    <div className={classes.container}>
      {!fetchError && <div className={classes.top}>
        <div className={classes.search}>
          <Search
            value={query}
            onChange={changeSearchHandler}
          />
        </div>
        <div className={classes.button}>
          <Button
            color={"red"}
            onClick={() => modalCreateToggle(true)}
            className={classes.button_add}
          >Добавить бригаду</Button>
        </div>
      </div>}
      <div className={classes.content}>
        {isLoading && <Loading/>}
        {dataTemplate}
        {emptyTemplate}
        {errorTemplate}
      </div>
      <div className={classes.bottom}>
        <Pagination
          onPageChange={pageChangeHandler}
          pageRangeDisplayed={5}
          pageCount={pages}
          forcePage={currentPage - 1}
        />
      </div>
      {modalCreate}
      {modalCreateSuccess}
      {modalUpdateSuccess}
      {modalDeleteSuccess}
    </div>
  )
}